import { FC, useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, Box, Typography, CircularProgress, Chip } from '@mui/material';
import { debounce } from 'lodash';
import { useLazyEmployerAdminSearchQuery } from '@carcareers/store';
import { Employer } from '@libs/models';

export interface EmployerOption {
  label: string;
  value: number;
  employer: Employer;
}

interface EmployerPostSelectProps {
  value?: EmployerOption | null;
  onChange?: (employer: EmployerOption | null) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

export const EmployerPostSelect: FC<EmployerPostSelectProps> = ({
  value,
  onChange,
  label = 'Select Employer',
  required = false,
  error = false,
  helperText,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<EmployerOption[]>([]);

  const [searchEmployers, { data, isFetching, isError }] = useLazyEmployerAdminSearchQuery();

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        searchEmployers({
          q: searchTerm,
          page: 1,
          limit: 25,
        });
      }, 300),
    [searchEmployers],
  );

  useEffect(() => {
    if (open) {
      if (inputValue.trim()) {
        debouncedSearch(inputValue);
      } else {
        searchEmployers({
          q: '',
          page: 1,
          limit: 25,
        });
      }
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, open, debouncedSearch, searchEmployers]);

  useEffect(() => {
    if (data?.items) {
      const employerOptions: EmployerOption[] = data.items.map((employer) => ({
        label:
          `${employer.user?.firstName || ''} ${employer.user?.lastName || ''}`.trim() ||
          employer.user?.email ||
          `Employer #${employer.id}`,
        value: employer.id,
        employer,
      }));
      setOptions(employerOptions);
    }
  }, [data]);

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: EmployerOption | null) => {
    onChange?.(newValue);
  };

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: EmployerOption) => (
    <Box component="li" {...props} sx={{ display: 'flex', gap: 1 }}>
      <Typography variant="body1" fontWeight="medium">
        {option.label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {option.employer.user?.email}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
        {option.employer.activeJobs !== undefined && (
          <Chip label={`${option.employer.activeJobs} Active Jobs`} size="small" color="primary" variant="outlined" />
        )}
        {(option.employer as any).rooftopsOnboarded !== undefined && (
          <Chip
            label={`${(option.employer as any).rooftopsOnboarded} Rooftops`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      options={options}
      loading={isFetching}
      loadingText="Searching employers..."
      noOptionsText={
        isError ? 'Error loading employers' : inputValue ? 'No employers found' : 'Start typing to search employers'
      }
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      filterOptions={(x) => x}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            sx: {
              borderRadius: 3,
            },
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={renderOption}
      sx={{ minWidth: 300 }}
    />
  );
};

export default EmployerPostSelect;
